import { createContext, ReactNode, useContext, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { PausedJobMoveDialog } from "@/components/companies/jobs/PausedJobMoveDialog";

import { useUpdateJobPause } from "@/hooks/mutations/useUpdateJobPause";
import {
  jobPauseStateQueryKey,
  useFetchJobPauseState,
} from "@/hooks/queries/useGetJobPauseState";
import {
  PRE_SUBMISSION_CANDIDACY_STATUSES,
  TERMINAL_CANDIDACY_STATUSES,
} from "@/lib/constants";
import { CandidacyStatus, JobPauseType } from "@/types";

export enum PausedJobMoveDecision {
  CANCEL = "CANCEL",
  MOVE_ONLY = "MOVE_ONLY",
  MOVE_AND_ACTIVATE = "MOVE_AND_ACTIVATE",
}

interface PausedJobMoveGate {
  // Resolves MOVE_ONLY straight away for any move that doesn't need asking, so
  // callers can await unconditionally.
  requestDecision: (args: {
    candidacyId: string;
    fromStatus?: CandidacyStatus;
    jobId: string;
    toStatus?: CandidacyStatus;
  }) => Promise<PausedJobMoveDecision>;
}

const PausedJobMoveGateContext = createContext<PausedJobMoveGate | null>(null);

/**
 * Shell contract: every pipeline move runs through here so a parked job gets
 * one chance to be reactivated, whatever surface the move came from — app
 * review, the candidate sheet, the kanban, the screening card.
 *
 * Deliberately one provider rather than a dialog per surface: a new call site
 * inherits the gate instead of having to remember it.
 */
export const PausedJobMoveGateProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const fetchJobPauseState = useFetchJobPauseState();
  const queryClient = useQueryClient();
  const { mutateAsync: updateJobPause } = useUpdateJobPause();
  // Covers the unpause and the refetches behind it, so the dialog holds until
  // the job status the caller is about to render has actually landed.
  const [isActivating, setIsActivating] = useState(false);
  const [pauseType, setPauseType] = useState<JobPauseType | null>(null);
  const jobIdRef = useRef<string | null>(null);
  const answeredMoveRef = useRef<string | null>(null);
  const resolveRef = useRef<((decision: PausedJobMoveDecision) => void) | null>(
    null,
  );

  /* Helpers */
  const moveKey = (
    candidacyId: string,
    jobId: string,
    toStatus?: CandidacyStatus,
  ) => `${jobId}:${candidacyId}:${toStatus ?? ""}`;

  const settle = (decision: PausedJobMoveDecision) => {
    resolveRef.current?.(decision);
    resolveRef.current = null;
    jobIdRef.current = null;
    setPauseType(null);
  };

  /* Handlers */
  // The gate owns marking the job active, not its callers: the unpause endpoint
  // returns a bare job row, so the caches that render job status are refetched
  // rather than overwritten with it. A failed unpause cancels the move — better
  // than moving the candidacy and leaving the job wrongly reading as active.
  const handleMoveAndActivate = async () => {
    const jobId = jobIdRef.current;
    if (!jobId) return;

    setIsActivating(true);
    try {
      await updateJobPause({ jobId, pause: false });
    } catch {
      setIsActivating(false);
      settle(PausedJobMoveDecision.CANCEL);
      return;
    }

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["job"] }),
      queryClient.invalidateQueries({ queryKey: ["jobs"] }),
      queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
      queryClient.invalidateQueries({
        queryKey: jobPauseStateQueryKey(jobId),
      }),
    ]);
    setIsActivating(false);
    settle(PausedJobMoveDecision.MOVE_AND_ACTIVATE);
  };

  // Only an advance out of a pre-submission stage counts. Sideways-to-terminal
  // is clearing the job out, and anything past Enrich and Check is beyond what
  // a pause holds up.
  const needsDecision = (
    fromStatus?: CandidacyStatus,
    toStatus?: CandidacyStatus,
  ) => {
    if (!toStatus || !fromStatus) return false;
    if (toStatus === fromStatus) return false;
    if (!PRE_SUBMISSION_CANDIDACY_STATUSES.includes(fromStatus)) return false;
    return !TERMINAL_CANDIDACY_STATUSES.includes(toStatus);
  };

  const requestDecision: PausedJobMoveGate["requestDecision"] = async ({
    candidacyId,
    fromStatus,
    jobId,
    toStatus,
  }) => {
    if (!needsDecision(fromStatus, toStatus)) {
      return PausedJobMoveDecision.MOVE_ONLY;
    }

    const key = moveKey(candidacyId, jobId, toStatus);
    if (answeredMoveRef.current === key) {
      answeredMoveRef.current = null;
      return PausedJobMoveDecision.MOVE_ONLY;
    }

    const pauseState = await fetchJobPauseState(jobId);
    if (!pauseState?.pausedAt) {
      return PausedJobMoveDecision.MOVE_ONLY;
    }

    // A pause predating pauseType reads as PAUSED, same as everywhere else.
    answeredMoveRef.current = key;
    jobIdRef.current = jobId;
    setPauseType(pauseState.pauseType ?? JobPauseType.PAUSED);
    return new Promise<PausedJobMoveDecision>((resolve) => {
      resolveRef.current = resolve;
    });
  };

  /* Render */
  const render = () => (
    <PausedJobMoveGateContext.Provider value={{ requestDecision }}>
      {children}
      {pauseType ? (
        <PausedJobMoveDialog
          isLoading={isActivating}
          onKeepParked={() => {
            settle(PausedJobMoveDecision.MOVE_ONLY);
          }}
          onMoveAndActivate={() => {
            void handleMoveAndActivate();
          }}
          onOpenChange={(isOpen) => {
            if (!isOpen) settle(PausedJobMoveDecision.CANCEL);
          }}
          open
          pauseType={pauseType}
        />
      ) : null}
    </PausedJobMoveGateContext.Provider>
  );

  return render();
};

// Null outside the provider — the gate is a shell concern, and a consumer
// rendered without it should move the candidacy rather than block on nothing.
export const usePausedJobMoveGate = () => useContext(PausedJobMoveGateContext);
